import { useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import Loading from 'components/Loading';
import MissingQuestionPager from 'components/MissingQuestionPager';
import { useUser } from '../context/userContext';
import { useGetQuestions } from '../api/hooks/useSurveyQuestions';
import { allQuestionsAnswered, isAnswerEmpty, isBoolean } from '../utils';
import { AnswersItem, Maybe, QuestionItem } from '../__generated__/graphql';

const IncompleteSurvey = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { companySlug } = useParams();
  const { answers, answerTimes, saveUserAnswers, company, userLoading, jobRoleId } = useUser();
  const { questions, questionLoading } = useGetQuestions({ companyId: company?.id || '', jobRoleId });

  const missingQuestions = useMemo(() => {
    if (!questions) return [] as Maybe<QuestionItem>[];
    const order: Array<keyof typeof questions> = ['personality', 'interests', 'values'];
    const list: Maybe<QuestionItem>[] = [];
    order.forEach((section) => {
      (questions[section] || []).forEach((q) => {
        const qid = q?.id;
        if (!qid) return;
        const val = (answers as unknown as Record<string, boolean | null | undefined>)?.[qid];
        if (!isBoolean(val)) list.push(q);
      });
    });
    return list;
  }, [questions, answers]);

  useEffect(() => {
    // If somehow everything is complete already, bounce to results intro
    if (questions && allQuestionsAnswered(questions, answers) && companySlug) {
      navigate(`/${companySlug}/results-intro`);
    }
  }, [answers, questions, companySlug, navigate]);

  const onCompleteMissing = useCallback(
    (subset: AnswersItem) => {
      const storedAnswers: AnswersItem = { ...(answers || {}) } as AnswersItem;
      const keys = Object.keys(subset) as (keyof AnswersItem)[];
      keys.forEach((k) => {
        storedAnswers[k] = subset[k];
      });
      // Save merged answers; keep existing answerTimes unchanged
      saveUserAnswers(storedAnswers, answerTimes || {});
      const searchParams = location.search;
      navigate(`/${companySlug}/results-intro${searchParams}`);
    },
    [answers, answerTimes, saveUserAnswers, navigate, location.search, companySlug],
  );

  if (userLoading || questionLoading) {
    return <Loading />;
  }

  // If no backend answers at all, send them to start
  if (isAnswerEmpty(answers)) {
    const searchParams = location.search;
    return (
      <div className="flex flex-col items-center justify-center gap-8 text-center text-white h-5/6">
        <div className="ps-3">
          <div className="mb-2 text-xl font-semibold text-white sm:text-display-sm leading-xl sm:leading-display-sm">Start your survey</div>
          <p className="text-white">We couldn\'t find previous answers. Let\'s get started.</p>
        </div>
        <button
          className="bg-yellow-500 text-black  p-2 uppercase rounded-lg font-semibold text-md"
          type="button"
          onClick={() => navigate(`/${companySlug}/profile${searchParams}`)}
        >
          Start
        </button>
      </div>
    );
  }

  // If there are no missing questions, go to results intro
  if (questions && missingQuestions.length === 0) {
    const searchParams = location.search;
    navigate(`/${companySlug}/results-intro${searchParams}`);
    return null;
  }

  return (
    <div className="flex flex-col items-center justify-between text-center text-white h-5/6">
      <img className="m-4 text-white w-14 h-14" src="/icon/puzzle_head.png" alt="puzzle head" />
      <div className="mb-1 font-semibold text-white text-l sm:text-display-sm leading-xl sm:leading-display-sm">Final questions...</div>
      <MissingQuestionPager questions={missingQuestions} onComplete={onCompleteMissing} />
    </div>
  );
};

export default IncompleteSurvey;
